import React, { useEffect, useState } from "react";
import { FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Undo2 } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { OfferFormValues } from "../../../schemas/offerFormSchema";
import { RegularOfferIncrementAlternative } from "@/types/campLocation";
import { getDecimalEnums } from "@/services/decimalEnumService";
import { db } from "@/services/firebaseService";
import { Offer } from "@/types/offer";
import { getSessionOwnNumDays, isNumDaysWithheld } from "../../utils/submissionUtils";

interface TimingSectionProps {
  form: UseFormReturn<OfferFormValues>;
  markFieldAsModified?: (fieldName: string) => void;
  isFieldModified?: (fieldName: string) => boolean;
  resetFieldToProgram?: (fieldName: string) => void;
  showHeaders?: boolean;
  hasInitialOffer?: boolean;
  initialOffer?: Offer | null;
}

export const TimingSection: React.FC<TimingSectionProps> = ({
  form,
  markFieldAsModified = () => {},
  isFieldModified = () => false,
  resetFieldToProgram = () => {},
  showHeaders = true,
  hasInitialOffer = false,
  initialOffer = null
}) => {
  // Get values from form
  const variation = form.watch("variation") as any;
  const numDaysOverride = form.watch("numDaysOverride");
  const timingNoteOverride = form.watch("timingNoteOverride");
  
  // State to track program values for display with unique keys for re-rendering
  const [programNumDays, setProgramNumDays] = useState<number | null | undefined>(null);
  const [programTimingNote, setProgramTimingNote] = useState<string>("");
  const [programValuesKey, setProgramValuesKey] = useState<number>(0);
  const [programIsAlternative, setProgramIsAlternative] = useState<boolean>(false);
  const [enumsMap, setEnumsMap] = useState<Map<string, any>>(new Map());
  
  // Load decimal enums for alternative pricing detection
  useEffect(() => {
    const loadEnums = async () => {
      try {
        const enums = await getDecimalEnums(db, "offerIncrement");
        const enumMap = new Map();
        enums.forEach(enumItem => {
          enumMap.set(enumItem.id, enumItem);
        });
        setEnumsMap(enumMap);
      } catch (error) {
        console.error("[TimingSection] Error loading decimal enums:", error);
      }
    };
    loadEnums();
  }, []);
  
  // Function to find matching alternative pricing based on numDays
  const findMatchingAlternative = (variationData: any, targetNumDays: number | null): RegularOfferIncrementAlternative | null => {
    if (!variationData?.regularOfferIncrementAlternatives || targetNumDays === null || targetNumDays === undefined) {
      return null;
    }
    
    for (const alternative of variationData.regularOfferIncrementAlternatives) {
      const enumItem = enumsMap.get(alternative.key);
      if (enumItem && enumItem.value === targetNumDays) {
        return alternative;
      }
    }
    
    return null;
  };
  
  // Function to check if a numDays value is a valid program value (base or alternative)
  const isValidProgramValue = (variationData: any, targetNumDays: number | null): boolean => {
    if (targetNumDays === null || targetNumDays === undefined) {
      return false;
    }
    
    // Check if it matches the base program value
    let baseProgramNumDays = null;
    if (variationData.regularOfferIncrementKey && enumsMap.has(variationData.regularOfferIncrementKey)) {
      baseProgramNumDays = enumsMap.get(variationData.regularOfferIncrementKey)?.value || null;
    } else if (variationData.numDays !== undefined) {
      baseProgramNumDays = variationData.numDays;
    }
    
    if (targetNumDays === baseProgramNumDays) {
      return true;
    }
    
    // Check if it matches any alternative value
    const matchingAlternative = findMatchingAlternative(variationData, targetNumDays);
    return !!matchingAlternative;
  };
  
  // What the field holds when the session has no override. An existing camp-data session shows its OWN
  // length (KB-4888: the program's default is wrong for every short or holiday week); a withheld one has no
  // length to show; any other session starts from the program.
  const prefillNumDays = (variationData: any): number | null => {
    if (hasInitialOffer && initialOffer) {
      if (isNumDaysWithheld(initialOffer)) return null;
      const ownNumDays = getSessionOwnNumDays(initialOffer);
      if (ownNumDays !== null) return ownNumDays;
    }
    if (variationData.regularOfferIncrementKey && enumsMap.has(variationData.regularOfferIncrementKey)) {
      return enumsMap.get(variationData.regularOfferIncrementKey)?.value || null;
    }
    return variationData.numDays !== undefined ? variationData.numDays : null;
  };

  // Function to refresh all program values from the variation
  const refreshProgramValues = (variationData?: any) => {
    const currentVariation = variationData || variation;
    
    if (currentVariation && enumsMap.size > 0) {
      // Calculate base program numDays
      let baseProgramNumDays = null;
      if (currentVariation.regularOfferIncrementKey && enumsMap.has(currentVariation.regularOfferIncrementKey)) {
        baseProgramNumDays = enumsMap.get(currentVariation.regularOfferIncrementKey)?.value || null;
      } else if (currentVariation.numDays !== undefined) {
        baseProgramNumDays = currentVariation.numDays;
      }
      
      // Check if current numDays matches an alternative
      const matchingAlternative = findMatchingAlternative(currentVariation, numDaysOverride);
      
      let effectiveNumDays: number | null;
      let isAlternative = false;
      
      if (matchingAlternative) {
        // Use alternative numDays (which is the current numDaysOverride that matches)
        effectiveNumDays = numDaysOverride;
        isAlternative = true;
      } else {
        // Use base program numDays
        effectiveNumDays = baseProgramNumDays;
        isAlternative = false;
      }
      
      // Program timing note is always from base program (alternatives don't have timing notes)
      const newProgramTimingNote = currentVariation.timings?.timingNote || "";
      
      
      // Update state values
      setProgramNumDays(effectiveNumDays);
      setProgramTimingNote(newProgramTimingNote);
      setProgramIsAlternative(isAlternative);
      
      // Increment the key to force re-renders
      setProgramValuesKey(prev => prev + 1);
    }
  };
  
  // Listen for variation changes from the custom event
  useEffect(() => {
    const handleVariationChange = (event: CustomEvent) => {
      
      const variationData = event.detail?.variationData;
      if (variationData) {
        // Update program-derived values display only when enums are loaded
        if (enumsMap.size > 0) {
          refreshProgramValues(variationData);
          
          // Handle initial population of numDaysOverride when selector is visible
          const currentNumDays = form.getValues("numDaysOverride");
          if (currentNumDays === null || currentNumDays === undefined || (!hasInitialOffer && !isFieldModified("numDaysOverride"))) {
            form.setValue("numDaysOverride", prefillNumDays(variationData), { shouldDirty: true });
          }
        }

        // Timing note population does not depend on enums; handle independently
        const currentTimingNote = form.getValues("timingNoteOverride");
        const programNote = variationData.timings?.timingNote || "";
        const shouldPopulateTimingNote =
          (!hasInitialOffer && (currentTimingNote === null || currentTimingNote === undefined || currentTimingNote === "")) ||
          (hasInitialOffer && (currentTimingNote === null || currentTimingNote === undefined));
        if (shouldPopulateTimingNote) {
          form.setValue("timingNoteOverride", programNote, { shouldDirty: true });
        }
      }
    };
    
    window.addEventListener('variationChanged', handleVariationChange as EventListener);
    return () => window.removeEventListener('variationChanged', handleVariationChange as EventListener);
  }, [form, isFieldModified, enumsMap, initialOffer]);
  
  // Listen for numDaysOverride changes to update program values display
  useEffect(() => {
    if (variation && enumsMap.size > 0) {
      refreshProgramValues();
    }
  }, [numDaysOverride, variation, enumsMap]);
  
  // Update local state when variation changes directly (not through event)
  useEffect(() => {
    if (variation) {
      // Refresh program-derived values only when enums available
      if (enumsMap.size > 0) {
        refreshProgramValues();
        const currentNumDays = form.getValues("numDaysOverride");
        if (currentNumDays === null || currentNumDays === undefined) {
          form.setValue("numDaysOverride", prefillNumDays(variation));
        }
      }

      // Always handle timing note population independent of enums
      const currentTimingNote = form.getValues("timingNoteOverride");
      const programNote = variation.timings?.timingNote || "";
      const shouldPopulateTimingNote =
        (!hasInitialOffer && (currentTimingNote === null || currentTimingNote === undefined || currentTimingNote === "")) ||
        (hasInitialOffer && (currentTimingNote === null || currentTimingNote === undefined));
      if (shouldPopulateTimingNote) {
        form.setValue("timingNoteOverride", programNote);
      }
    }
  }, [variation, enumsMap, form, initialOffer]);
  
  // Check if values differ from program values for conditional UI
  // IMPORTANT: Only show reset button if the current value is NOT a valid program value
  const isNumDaysDifferent = variation && enumsMap.size > 0 
    ? !isValidProgramValue(variation, numDaysOverride) && numDaysOverride !== null && numDaysOverride !== undefined
    : numDaysOverride !== programNumDays && numDaysOverride !== null && numDaysOverride !== undefined;
  
  // For timing note, show reset when: timingNoteOverride is not null AND differs from program value
  const isTimingNoteDifferent = timingNoteOverride !== null && timingNoteOverride !== programTimingNote;
  
  
  return (
    <div className="space-y-4">
      {showHeaders && <h3 className="text-lg font-medium">Timing</h3>}
      
      <FormField
        control={form.control}
        name="numDaysOverride"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between">
              <FormLabel>Number of days</FormLabel>
              {isNumDaysDifferent && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => resetFieldToProgram("numDaysOverride")}
                  className="h-6 px-2"
                >
                  <Undo2 className="h-3 w-3 mr-1" />
                  Reset to program
                </Button>
              )}
            </div>
            <Input
              type="number"
              value={field.value === null || field.value === undefined ? "" : field.value}
              onChange={e => {
                const value = e.target.value === "" ? null : Number(e.target.value);
                field.onChange(value);
                markFieldAsModified("numDaysOverride");
              }}
              onBlur={e => {
                // A withheld session opens with this field empty on purpose; tabbing through it must not
                // turn the withhold into a 0-day override.
                const untouchedWithheld = hasInitialOffer && isNumDaysWithheld(initialOffer) && !isFieldModified("numDaysOverride");
                if (e.target.value === "" && !untouchedWithheld) {
                  field.onChange(0);
                  markFieldAsModified("numDaysOverride");
                }
              }}
              placeholder=""
              min="0"
              step="1"
            />
            {/* Enhanced program value footnote that shows alternative vs base program value */}
            <div className="text-xs text-muted-foreground mt-1" key={`days-${programValuesKey}`}>
              {programIsAlternative 
                ? `Program's alternative value: ${programNumDays !== undefined && programNumDays !== null ? programNumDays : "empty"}`
                : `Program's value: ${programNumDays !== undefined && programNumDays !== null ? programNumDays : "empty"}`
              }
            </div>
          </FormItem>
        )}
      />
      
      <FormField
        control={form.control}
        name="timingNoteOverride"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between">
              <FormLabel>Timing note</FormLabel>
              {/* Show reset button if note is not null and differs from program value */}
              {isTimingNoteDifferent && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => resetFieldToProgram("timingNoteOverride")}
                  className="h-6 px-2"
                >
                  <Undo2 className="h-3 w-3 mr-1" />
                  Reset to program
                </Button>
              )}
            </div>
            <Textarea
              value={
                // null = show program value, anything else = show the actual value (including "")
                field.value === null ? programTimingNote : field.value
              }
              placeholder="Example: Fridays end at 3:00"
              onChange={e => {
                field.onChange(e.target.value);
                markFieldAsModified("timingNoteOverride");
              }}
              rows={2}
            />
            {/* Show program value footnote - timing notes don't have alternatives */}
            <div className="text-xs text-muted-foreground mt-1" key={`timing-${programValuesKey}`}>
              Program's value: {programTimingNote ? `"${programTimingNote}"` : "empty"}
              {field.value !== null && (
                <span className="ml-2">
                  | Current override: {field.value === "" ? "empty string" : `"${field.value}"`}
                </span>
              )}
            </div>
          </FormItem>
        )}
      />
    </div>
  );
};
